/**
 * HTML <-> Slate value serialization for the email editor.
 * Handles: p, h1-h3, br, strong/b, em/i, u, s, a, img, ul, ol, li, blockquote, table, div (with align/style).
 */
import type { Descendant } from 'slate';
import { Element as SlateElement, Text } from 'slate';
import type { CustomElement, CustomText } from './slate-types';

const DEFAULT_VALUE: Descendant[] = [{ type: 'paragraph', children: [{ text: '' }] }];

function createText(text: string, marks: Partial<CustomText> = {}): CustomText {
  const t: CustomText = { text };
  if (marks.bold) t.bold = true;
  if (marks.italic) t.italic = true;
  if (marks.underline) t.underline = true;
  if (marks.strikethrough) t.strikethrough = true;
  if (marks.color) t.color = marks.color;
  if (marks.backgroundColor) t.backgroundColor = marks.backgroundColor;
  if (marks.fontFamily) t.fontFamily = marks.fontFamily;
  if (marks.fontSize) t.fontSize = marks.fontSize;
  return t;
}

function getAlign(el: Element): 'left' | 'center' | 'right' | 'justify' | undefined {
  const align = el.getAttribute('align') || (el as HTMLElement).style?.textAlign;
  if (align === 'center' || align === 'right' || align === 'justify' || align === 'left') return align;
  return undefined;
}

type InlineChild = CustomText | { type: 'link'; url: string; children: CustomText[] } | { type: 'image'; url: string; alt?: string; width?: string; height?: string };

function parseInlineNodes(nodes: NodeList | Node[], marks: Partial<CustomText> = {}): InlineChild[] {
  const result: InlineChild[] = [];
  const walk = (list: NodeList | Node[]) => {
    for (let i = 0; i < list.length; i++) {
      const node = list[i];
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (text) result.push(createText(text, marks));
        continue;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      const nextMarks = { ...marks };
      if (tag === 'strong' || tag === 'b') nextMarks.bold = true;
      else if (tag === 'em' || tag === 'i') nextMarks.italic = true;
      else if (tag === 'u') nextMarks.underline = true;
      else if (tag === 's' || tag === 'strike') nextMarks.strikethrough = true;
      else if (tag === 'span') {
        const style = (el as HTMLElement).style;
        if (style?.color) nextMarks.color = style.color;
        if (style?.backgroundColor) nextMarks.backgroundColor = style.backgroundColor;
        if (style?.fontFamily) nextMarks.fontFamily = style.fontFamily;
        if (style?.fontSize) nextMarks.fontSize = style.fontSize;
      }
      else if (tag === 'a') {
        const url = el.getAttribute('href') || '';
        const childNodes = parseInlineNodes(el.childNodes, nextMarks).filter((n): n is CustomText => 'text' in n);
        if (childNodes.length) result.push({ type: 'link', url, children: childNodes });
        else result.push({ type: 'link', url, children: [createText(url, nextMarks)] });
        continue;
      }
      else if (tag === 'img') {
        const imgEl = el as HTMLElement;
        const url = el.getAttribute('src') || '';
        const alt = el.getAttribute('alt') || undefined;
        const width = imgEl.style?.width || el.getAttribute('width') || undefined;
        const height = imgEl.style?.height || el.getAttribute('height') || undefined;
        result.push({ type: 'image', url, alt, width: width || undefined, height: height || undefined });
        continue;
      }
      if (tag === 'br') result.push(createText('\n', marks));
      else walk(el.childNodes);
    }
  };
  walk(nodes);
  return result;
}

function parseBlock(el: Element): CustomElement | null {
  const tag = el.tagName.toLowerCase();
  const align = getAlign(el);

  if (tag === 'div' && el.getAttribute('data-slate-quoted-rich') === 'true') {
    const raw = el.getAttribute('data-html') || '';
    try {
      const innerHtml = raw ? decodeURIComponent(raw) : '';
      return {
        type: 'quoted-rich-html',
        html: innerHtml,
        children: [{ text: '' }],
      } as CustomElement;
    } catch {
      return { type: 'quoted-rich-html', html: '', children: [{ text: '' }] } as CustomElement;
    }
  }

  if (tag === 'div' && el.hasAttribute('data-slate-raw-html')) {
    const raw = el.getAttribute('data-html') || '';
    const inline = el.hasAttribute('data-slate-inline-html');
    try {
      const html = raw ? decodeURIComponent(raw) : '';
      return {
        type: 'raw-html',
        html,
        ...(inline ? { inline: true } : {}),
        children: [{ text: '' }],
      } as CustomElement;
    } catch {
      return { type: 'raw-html', html: '', children: [{ text: '' }] } as CustomElement;
    }
  }

  if (tag === 'div' && el.getAttribute('data-ums-reply-header') === 'true') {
    const styleAttr = el.getAttribute('style') || '';
    const childBlocks = Array.from(el.children)
      .map((c) => parseBlock(c))
      .filter((n): n is CustomElement => n != null) as Descendant[];
    return {
      type: 'reply-header-box',
      style: styleAttr,
      children: childBlocks.length ? childBlocks : [{ type: 'paragraph', children: [{ text: '' }] }],
    } as CustomElement;
  }

  if (tag === 'p' || tag === 'div') {
    const inlines = parseInlineNodes(el.childNodes);
    const children: Descendant[] = inlines.length
      ? inlines.map((n) => {
          if ('text' in n) return n;
          if (n.type === 'link') return { type: 'link' as const, url: n.url, children: n.children };
          if (n.type === 'image') return { type: 'image' as const, url: n.url, alt: n.alt, width: n.width, height: n.height, children: [{ text: '' }] };
          return createText('');
        })
      : [createText('')];
    return { type: 'paragraph', align, children } as CustomElement;
  }
  if (tag === 'h1') return { type: 'heading-one', align, children: [{ text: (el.textContent || '').trim() || ' ' }] } as CustomElement;
  if (tag === 'h2') return { type: 'heading-two', align, children: [{ text: (el.textContent || '').trim() || ' ' }] } as CustomElement;
  if (tag === 'h3') return { type: 'heading-three', align, children: [{ text: (el.textContent || '').trim() || ' ' }] } as CustomElement;
  if (tag === 'blockquote') {
    const children = Array.from(el.children).map((c) => parseBlock(c)).filter((n): n is CustomElement => n != null) as Descendant[];
    return { type: 'block-quote', children: children.length ? children : [{ type: 'paragraph', children: [{ text: '' }] }] } as CustomElement;
  }
  if (tag === 'ul') {
    const items = Array.from(el.querySelectorAll(':scope > li')).map((li) => {
      const inlines = parseInlineNodes(li.childNodes);
      const itemChildren = inlines.length
        ? inlines.map((n) => {
            if ('text' in n) return n;
            if (n.type === 'link') return { type: 'link' as const, url: n.url, children: n.children };
            if (n.type === 'image') return { type: 'image' as const, url: n.url, alt: n.alt, width: n.width, height: n.height, children: [{ text: '' }] };
            return createText('');
          })
        : [createText('')];
      return { type: 'list-item', children: [{ type: 'paragraph', children: itemChildren }] } as CustomElement;
    });
    return { type: 'bulleted-list', children: items.length ? items : [{ type: 'list-item', children: [{ type: 'paragraph', children: [{ text: '' }] }] }] } as CustomElement;
  }
  if (tag === 'ol') {
    const items = Array.from(el.querySelectorAll(':scope > li')).map((li) => {
      const inlines = parseInlineNodes(li.childNodes);
      const itemChildren = inlines.length
        ? inlines.map((n) => {
            if ('text' in n) return n;
            if (n.type === 'link') return { type: 'link' as const, url: n.url, children: n.children };
            if (n.type === 'image') return { type: 'image' as const, url: n.url, alt: n.alt, width: n.width, height: n.height, children: [{ text: '' }] };
            return createText('');
          })
        : [createText('')];
      return { type: 'list-item', children: [{ type: 'paragraph', children: itemChildren }] } as CustomElement;
    });
    return { type: 'numbered-list', children: items.length ? items : [{ type: 'list-item', children: [{ type: 'paragraph', children: [{ text: '' }] }] }] } as CustomElement;
  }
  if (tag === 'table') {
    const rows: CustomElement[] = [];
    const trs = el.querySelectorAll(':scope > tbody > tr, :scope > tr');
    trs.forEach((tr) => {
      const cells: CustomElement[] = [];
      tr.querySelectorAll(':scope > td, :scope > th').forEach((td) => {
        const colSpan = parseInt(td.getAttribute('colspan') || '1', 10) || 1;
        const rowSpan = parseInt(td.getAttribute('rowspan') || '1', 10) || 1;
        const inlines = parseInlineNodes(td.childNodes);
        const cellChildren = inlines.length
          ? inlines.map((n) => {
              if ('text' in n) return n;
              if (n.type === 'link') return { type: 'link' as const, url: n.url, children: n.children };
              if (n.type === 'image') return { type: 'image' as const, url: n.url, alt: n.alt, width: n.width, height: n.height, children: [{ text: '' }] };
              return createText('');
            })
          : [createText('')];
        const tdEl = td as HTMLElement;
        const cellWidth = tdEl.getAttribute('width') || tdEl.style?.width || undefined;
        const cellNode = { type: 'table-cell', colSpan, rowSpan, children: [{ type: 'paragraph', children: cellChildren }] } as CustomElement;
        if (cellWidth) (cellNode as { width?: string }).width = cellWidth;
        cells.push(cellNode);
      });
      const rowHeight = (tr as HTMLElement).getAttribute('height') || (tr as HTMLElement).style?.height || undefined;
      const rowNode = { type: 'table-row' as const, children: cells } as CustomElement;
      if (rowHeight) (rowNode as { height?: string }).height = rowHeight;
      if (cells.length) rows.push(rowNode);
    });
    const width = el.getAttribute('width') || (el as HTMLElement).style?.width || undefined;
    const height = el.getAttribute('height') || (el as HTMLElement).style?.height || undefined;
    const tableNode = { type: 'table' as const, children: rows.length ? rows : [{ type: 'table-row', children: [{ type: 'table-cell', children: [{ type: 'paragraph', children: [{ text: '' }] }] }] }] } as CustomElement;
    if (width) (tableNode as { width?: string }).width = width;
    if (height) (tableNode as { height?: string }).height = height;
    return tableNode;
  }
  if (tag === 'img') {
    const imgEl = el as HTMLElement;
    const url = el.getAttribute('src') || '';
    const alt = el.getAttribute('alt') || '';
    const width = imgEl.style?.width || el.getAttribute('width') || undefined;
    const height = imgEl.style?.height || el.getAttribute('height') || undefined;
    return { type: 'image', url, alt, width, height, children: [{ text: '' }] } as CustomElement;
  }
  return null;
}

/**
 * Deserialize HTML string to Slate Descendant[].
 * When we meet a div with element children (e.g. email-signature wrapper), recurse so
 * inner blocks (p, img, etc.) are parsed and images are not lost.
 */
export function htmlToSlate(html: string): Descendant[] {
  if (!html || !html.trim()) return DEFAULT_VALUE;
  const div = typeof document !== 'undefined' ? document.createElement('div') : null;
  if (!div) return DEFAULT_VALUE;
  div.innerHTML = html.trim();
  const blocks: Descendant[] = [];
  const walk = (parent: Element) => {
    for (let i = 0; i < parent.children.length; i++) {
      const el = parent.children[i];
      const tag = el.tagName.toLowerCase();
      if (tag === 'div' && el.getAttribute('data-ums-reply-header') === 'true') {
        const block = parseBlock(el);
        if (block) blocks.push(block);
        continue;
      }
      if (tag === 'div' && el.getAttribute('data-slate-quoted-rich') === 'true') {
        const block = parseBlock(el);
        if (block) blocks.push(block);
        continue;
      }
      if (tag === 'br') {
        blocks.push({ type: 'paragraph', children: [{ text: '\n' }] } as CustomElement);
        continue;
      }
      if (tag === 'div' && el.children.length > 0) {
        walk(el);
        continue;
      }
      const block = parseBlock(el);
      if (block) {
        blocks.push(block);
        continue;
      }
      if (el.children.length > 0) {
        walk(el);
      }
    }
  };
  walk(div);
  if (blocks.length === 0) return DEFAULT_VALUE;
  return blocks;
}

function serializeMarks(text: CustomText): string {
  let s = escapeHtml(text.text).replace(/\n/g, '<br />');
  if (text.bold) s = `<strong>${s}</strong>`;
  if (text.italic) s = `<em>${s}</em>`;
  if (text.underline) s = `<u>${s}</u>`;
  if (text.strikethrough) s = `<s>${s}</s>`;
  const spanParts: string[] = [];
  if (text.color) spanParts.push(`color:${text.color}`);
  if (text.backgroundColor) spanParts.push(`background-color:${text.backgroundColor}`);
  if (text.fontFamily) spanParts.push(`font-family:${text.fontFamily}`);
  if (text.fontSize) spanParts.push(`font-size:${text.fontSize}`);
  if (spanParts.length) s = `<span style="${spanParts.join(';')}">${s}</span>`;
  return s;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function serializeInlines(children: Descendant[]): string {
  return children.map((node) => {
    if (Text.isText(node)) return serializeMarks(node as CustomText);
    const n = node as CustomElement;
    if (n.type === 'link') return `<a href="${escapeHtml(n.url)}" target="_blank" rel="noopener noreferrer">${serializeInlines(n.children)}</a>`;
    if (n.type === 'image') {
      const img = n as { url: string; alt?: string; width?: string; height?: string };
      const styleParts: string[] = [];
      if (img.width) styleParts.push(`width: ${img.width}`);
      if (img.height) styleParts.push(`height: ${img.height}`);
      const styleAttr = styleParts.length ? ` style="${styleParts.join('; ')}"` : '';
      return `<img src="${escapeHtml(img.url)}" alt="${escapeHtml(img.alt || '')}"${styleAttr} />`;
    }
    return serializeInlines(n.children);
  }).join('');
}

function serializeBlock(node: CustomElement): string {
  const children = node.children as Descendant[];
  const inner = () => serializeInlines(children);
  const align = (node as { align?: string }).align;
  const alignAttr = align ? ` align="${align}"` : '';

  switch (node.type) {
    case 'paragraph':
      return `<p${alignAttr}>${inner() || '<br />'}</p>`;
    case 'heading-one':
      return `<h1${alignAttr}>${inner() || ' '}</h1>`;
    case 'heading-two':
      return `<h2${alignAttr}>${inner() || ' '}</h2>`;
    case 'heading-three':
      return `<h3${alignAttr}>${inner() || ' '}</h3>`;
    case 'block-quote':
      return `<blockquote class="email-quoted-block">${children.map((c) => serializeBlock(c as CustomElement)).join('')}</blockquote>`;
    case 'reply-header-box': {
      const box = node as { type: 'reply-header-box'; style?: string; children: Descendant[] };
      const styleAttr = box.style ? ` style="${escapeHtml(box.style)}"` : '';
      const innerHtml = box.children.map((c) => serializeBlock(c as CustomElement)).join('');
      return `<div data-ums-reply-header="true"${styleAttr}>${innerHtml}</div>`;
    }
    case 'quoted-rich-html': {
      const q = node as { type: 'quoted-rich-html'; html: string };
      const encoded = encodeURIComponent(q.html || '');
      return `<div data-slate-quoted-rich="true" data-html="${encoded}"></div>`;
    }
    case 'raw-html': {
      const rawNode = node as { type: 'raw-html'; html: string; inline?: boolean };
      const html = rawNode.html || '';
      const encoded = encodeURIComponent(html);
      const inlineAttr = rawNode.inline ? ' data-slate-inline-html="true"' : '';
      return `<div data-slate-raw-html="true"${inlineAttr} data-html="${encoded}"></div>`;
    }
    case 'list-item': {
      const itemInner = children.map((c) => (SlateElement.isElement(c) && (c as CustomElement).type === 'paragraph' ? serializeInlines((c as CustomElement).children) : '')).join('');
      return `<li>${itemInner || '<br />'}</li>`;
    }
    case 'bulleted-list':
      return `<ul>${children.map((c) => serializeBlock(c as CustomElement)).join('')}</ul>`;
    case 'numbered-list':
      return `<ol>${children.map((c) => serializeBlock(c as CustomElement)).join('')}</ol>`;
    case 'table': {
      const tbl = node as { width?: string; height?: string };
      const styleParts: string[] = [];
      if (tbl.width) styleParts.push(`width: ${tbl.width}`);
      if (tbl.height) styleParts.push(`height: ${tbl.height}`);
      const styleAttr = styleParts.length ? ` style="${styleParts.join('; ')}"` : '';
      return `<table${styleAttr}><tbody>${children.map((c) => serializeBlock(c as CustomElement)).join('')}</tbody></table>`;
    }
    case 'table-row': {
      const row = node as { height?: string };
      const rowStyle = row.height ? ` style="height: ${row.height}; min-height: ${row.height}"` : '';
      return `<tr${rowStyle}>${children.map((c) => serializeBlock(c as CustomElement)).join('')}</tr>`;
    }
    case 'table-cell': {
      const cell = node as { colSpan?: number; rowSpan?: number; width?: string };
      const cspan = cell.colSpan && cell.colSpan > 1 ? ` colspan="${cell.colSpan}"` : '';
      const rspan = cell.rowSpan && cell.rowSpan > 1 ? ` rowspan="${cell.rowSpan}"` : '';
      const styleParts: string[] = [];
      if (cell.width) styleParts.push(`width: ${cell.width}`);
      const styleAttr = styleParts.length ? ` style="${styleParts.join('; ')}"` : '';
      const cellInner = children.map((c) => (SlateElement.isElement(c) && (c as CustomElement).type === 'paragraph' ? serializeInlines((c as CustomElement).children) : '')).join('');
      return `<td${cspan}${rspan}${styleAttr}>${cellInner || '<br />'}</td>`;
    }
    case 'image': {
      const imgNode = node as { url: string; alt?: string; width?: string; height?: string };
      const imgStyleParts: string[] = [];
      if (imgNode.width) imgStyleParts.push(`width: ${imgNode.width}`);
      if (imgNode.height) imgStyleParts.push(`height: ${imgNode.height}`);
      const imgStyleAttr = imgStyleParts.length ? ` style="${imgStyleParts.join('; ')}"` : '';
      return `<img src="${escapeHtml(imgNode.url)}" alt="${escapeHtml(imgNode.alt || '')}"${imgStyleAttr} />`;
    }
    default:
      return `<p>${inner() || '<br />'}</p>`;
  }
}

/**
 * Serialize Slate Descendant[] to HTML string.
 */
export function slateToHtml(value: Descendant[]): string {
  if (!value || value.length === 0) return '<p><br /></p>';
  return value.map((node) => serializeBlock(node as CustomElement)).join('');
}

export { DEFAULT_VALUE };
